import {
  type Job,
  createJob,
  getNextPendingJob,
  markJobCompleted,
  markJobFailed,
  markJobProcessing,
  markJobRetryable,
} from '@/modules/job-queue/db'
import { emit } from '@/modules/job-queue/event-bus'

export interface JobHandler {
  type: string
  process(job: Job): Promise<void>
}

const handlers = new Map<string, JobHandler>()
let processingPromise: Promise<void> | null = null

export async function enqueueJob(
  type: string,
  payload: Record<string, unknown>,
  maxAttempts = 5,
): Promise<string> {
  const job = await createJob(type, payload, maxAttempts)
  emit('job:queued', job)
  void processAllJobs()
  return job.id
}

export function registerHandler(handler: JobHandler): void {
  handlers.set(handler.type, handler)
}

export function clearJobHandlers(): void {
  handlers.clear()
}

export async function processNextJob(): Promise<boolean> {
  const pendingJob = await getNextPendingJob()
  if (!pendingJob) return false

  const handler = handlers.get(pendingJob.type)
  if (!handler) {
    const failed = await markJobFailed(pendingJob, `No handler for job type "${pendingJob.type}"`)
    emit('job:failed', failed)
    return true
  }

  const processingJob = await markJobProcessing(pendingJob)

  try {
    await handler.process(processingJob)
    const completed = await markJobCompleted(processingJob)
    emit('job:completed', completed)
    return true
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown queue error'
    if (processingJob.attempts >= processingJob.maxAttempts) {
      const failed = await markJobFailed(processingJob, message)
      emit('job:failed', failed)
      return true
    }

    const retried = await markJobRetryable(processingJob, message)
    emit('job:retry', retried)
    return true
  }
}

export async function processAllJobs(maxJobs = 50): Promise<void> {
  if (processingPromise) {
    await processingPromise
    return
  }

  processingPromise = (async () => {
    let processedCount = 0
    while (processedCount < maxJobs) {
      const didProcess = await processNextJob()
      if (!didProcess) break
      processedCount += 1
    }
  })()

  try {
    await processingPromise
  } finally {
    processingPromise = null
  }
}
